import {
  Button,
  Container,
  FormControl,
  FormHelperText,
  Grid,
  TextField,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { Theme, useTheme } from "@mui/material/styles";
import { DateTimePicker } from "@mui/x-date-pickers";
import ChangeReasonDialog from "components/ChangeReasonDialog";
import EntityDetails from "components/EntityDetails";
import RedirectBackButton from "components/RedirectBackButton";
import RowElement from "components/RowElement";
import SnackbarMessage from "components/SnackbarMessage";
import { makeStyles } from "helpers/StyleHelper";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useFormattedTitleFromUser from "hooks/useFormattedTitleFromUser";
import { pick } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { Field, withTypes } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import {
  CertifyUserSkuRequest,
  OrganizationUserDetails,
  ResponseCode,
  ResponseOfSkus,
  ResponseOfUserSkuExtended,
} from "services/KnowfullyAdminClient";
import { Severity, SnackbarMessageProps } from "types/common-types";
import { convertToUTC, standardDateTimeFormat } from "utils/dateUtils";

const useStyles = makeStyles((theme: Theme) => ({
  certifyCourse: {
    mb: theme.spacing(3),
  },
  certifyButton: {
    mt: theme.spacing(2),
  },
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
}));

type CertifyFormValues = Omit<
  CertifyUserSkuRequest,
  "skuId" | "userSkuId" | "userId" | "init" | "toJSON"
> & {
  changeReason: string;
};

const { Form } = withTypes<CertifyFormValues>();

const CertifyCoursePage = () => {
  const { userId, userSkuId } = useParams<{
    userId: string;
    userSkuId: string;
  }>();
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();

  const {
    organizationUserService,
    userSkuService,
    skuService,
    userCourseUrl,
  } = useGlobalContext();

  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const [certifyMessageSent, certifyMessageSentActions] = useBoolean(false);
  const [formValues, setFormValues] = useState<CertifyFormValues>();
  const [changeReason, setChangeReason] = useState("");
  const [changeReasonDialogOpen, setChangeReasonDialogOpen] = useState(false);

  const userResult = useQuery<OrganizationUserDetails>(
    ["getOrganizationUserDetails", userId],
    () => organizationUserService.getOrganizationUserDetails(userId!),
    {
      enabled: !!userId,
    }
  );

  const user = userResult?.data;
  useFormattedTitleFromUser("certifyCourseTitle", user, t({ id: "user" }));

  const userSkuResult = useQuery<ResponseOfUserSkuExtended>(
    ["getUserSku", userSkuId],
    () =>
      userSkuService.getUserSku(
        user?.id,
        user?.organizationId,
        userSkuId!
      ),
    { enabled: !!userResult && !userResult.isLoading }
  );

  const userSku = userSkuResult?.data?.data!;

  const skuResult = useQuery<ResponseOfSkus>(
    ["sku", userSku?.skuId],
    () => skuService.getSkuById(userSku?.skuId!),
    {
      enabled: !!userSku && !userSkuResult.isLoading,
    }
  );

  const sku = skuResult?.data?.data!;

  useEffect(() => {
    if (certifyMessageSent) {
      setSnackbarMessage(
        new SnackbarMessageProps(
          t({ id: "certifyUserSkuMessageSent" }),
          Severity.Success
        )
      );

      setChangeReasonDialogOpen(false);
    }
  }, [certifyMessageSent, changeReasonDialogOpen, t]);

  const onCertifySuccess = async () => {
    certifyMessageSentActions.setTrue();
    setChangeReasonDialogOpen(false);
    //after certify message is sent, waits for 5 seconds to invalidate the query.
    await new Promise((f) => setTimeout(f, 5000));
    queryClient.invalidateQueries(["getUserSku", userSkuId]);
  };

  const loading =
    userResult.isLoading || userSkuResult.isLoading || skuResult.isLoading;

  const rows = [
    RowElement(
      "userNameLabel",
      `${user?.firstName ?? ""} ${user?.lastName ?? ""}`,
      loading
    ),
    RowElement("emailLabel", user?.email!, loading),
    RowElement("userId", user?.id!, loading),
    RowElement("courseName", userSku?.title ?? "", loading),
    RowElement("skuLabel", sku?.sku ?? "", loading),
    RowElement("userSkuId", userSkuId!, loading),
  ].filter((r) => !!r.component);

  const init = useMemo<CertifyUserSkuRequest>(
    () =>
      CertifyUserSkuRequest.fromJS({
        ...pick(
          {
            creditsEarned: sku?.credits,
            completionDate: sku?.eventStartDate ?? new Date(),
          },
          ["creditsEarned", "completionDate"]
        ),
      }),
    [sku]
  );

  const onCertifyUserSku = async () => {
    if (!formValues) {
      return;
    }
    const completionDate = convertToUTC(formValues?.completionDate);
    const response = await userSkuService.certifyUserSku(
      new CertifyUserSkuRequest({
        ...formValues,
        userId: userId!,
        userSkuId: userSkuId!,
        skuId: userSku?.skuId,
        organizationId: user?.organizationId,
        completionDate,
        changeReason,
      })
    );
    if (response.responseCode === ResponseCode.OK) {
      await onCertifySuccess();
    }
  };

  if (userResult.isError || userSkuResult.isError || skuResult.isError) {
    return (
      <Alert severity="error">
        <pre>{t({ id: "fetchError" })}</pre>
      </Alert>
    );
  }

  return (
    <Container>
      <RedirectBackButton
        redirectRoute={userCourseUrl(userId, userSkuId)}
        labelKey="backToUserCourse"
      />

      <h1>{t({ id: "certifyCourseLabel" })}</h1>

      <EntityDetails
        title={t({ id: "registrationDetails" })}
        details={rows}
        sx={styles.certifyCourse}
      />

      <Form
        onSubmit={(values) => {
          setFormValues(values);
          setChangeReasonDialogOpen(true);
        }}
        initialValues={init}
        render={({ handleSubmit, submitting, submitError, values }) => (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={1}>
              <Field name="completionDate" required>
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <FormControl error={meta.error && meta.submitFailed}>
                      <DateTimePicker
                        label={t({ id: "dateOfCompletionLabel" })}
                        format={standardDateTimeFormat}
                        ampm={true}
                        slotProps={{
                          textField: {
                            required: true,
                            variant: "standard",
                            error: meta.error && meta.submitFailed,
                            helperText:
                              meta.error && meta.submitFailed && meta.error,
                          },
                          actionBar: {
                            actions: ["clear", "cancel"],
                          },
                        }}
                        {...input}
                        value={input.value === "" ? null : input.value}
                      />
                      {meta.error && meta.submitFailed && (
                        <FormHelperText>{meta.error}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                )}
              </Field>

              <Field name="creditsEarned" type="number">
                {({ input, meta }) => (
                  <Grid item xs={6}>
                    <TextField
                      variant="standard"
                      type="number"
                      label={t({ id: "credits" })}
                      error={meta.error && meta.submitFailed}
                      helperText={meta.error && meta.submitFailed}
                      disabled={true}
                      {...input}
                    />
                  </Grid>
                )}
              </Field>

              {submitError && (
                <Alert sx={styles.feedback} severity="error">
                  {submitError}
                </Alert>
              )}
            </Grid>

            <Grid item>
              <Button
                sx={styles.certifyButton}
                variant="outlined"
                disabled={
                  loading ||
                  submitting ||
                  values.completionDate == null ||
                  changeReasonDialogOpen
                }
                onClick={() => handleSubmit()}
              >
                <T id="certifyUserSkuLabel" />
              </Button>
            </Grid>
          </form>
        )}
      />

      <ChangeReasonDialog
        open={changeReasonDialogOpen}
        onCancel={() => setChangeReasonDialogOpen(false)}
        setChangeReason={setChangeReason}
        handleConfirm={onCertifyUserSku}
        changeReason={changeReason}
      />

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Container>
  );
};

export default CertifyCoursePage;
